/**
* Copyright by: 

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import {
  Component,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { ControlContainer, FormControl, FormGroup } from '@angular/forms';
import { faClock, faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { Subscription } from 'rxjs';
import { DayOfWeek } from '../../../../../../models/dtos/scheduled-task.dto';
import {
  ScheduledTaskService,
  weekdayModel
} from '../../../../../../services/scheduled-task.service';
import {
  getDateText,
  getLastPositionText,
  getPositionText,
  getWeekdayPositionInMonth,
  getYearlyDescription
} from '../../../../../shared/functions';

enum RepetitionOptionsEnum {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  WEEKDAILY = 'WEEKDAILY',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY'
}

enum MonthlyPatternType {
  POSITION = 'position',
  FOURTH = 'fourth',
  LAST = 'last',
  DATE = 'date'
}

/** Interface representing the structure of the scheduling form */
interface TaskSchedulingFormGroup {
  enableScheduling: FormControl<boolean | null>;
  scheduleDate: FormControl<string | null>;
  scheduleTime: FormControl<string | null>;
  repeatTaskToggle: FormControl<boolean | null>;
  recurring: FormControl<RepetitionOptionsEnum | '' | null>;
  every: FormControl<number | null>;
  weekdaysControls: FormGroup;
  monthlyPatternType: FormControl<MonthlyPatternType | null>;
}

@Component({
  selector: 'oneapp-task-manager-scheduling',
  templateUrl: './task-scheduling.component.html',
  styleUrl: './task-scheduling.component.scss',
  standalone: false
})
export class TaskSchedulingComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  locked: boolean = false;

  faClock = faClock;
  faMagnifyingGlass = faMagnifyingGlass;

  taskScheduledInPast: boolean = false;
  minScheduleDate: Date = new Date();
  recurringFrequency: {
    key: string;
    value: string;
  }[] = [
    { key: RepetitionOptionsEnum.DAILY, value: $localize`:day@@dropdownDay:day(s)` },
    {
      key: RepetitionOptionsEnum.WEEKDAILY,
      value: $localize`:weekday@@dropdownWeekday:weekday`
    },
    { key: RepetitionOptionsEnum.WEEKLY, value: $localize`:week@@dropdownWeek:week(s)` },
    { key: RepetitionOptionsEnum.MONTHLY, value: $localize`:month@@dropdownMonth:month(s)` },
    { key: RepetitionOptionsEnum.YEARLY, value: $localize`:year@@dropdownYear:year(s)` }
  ];
  weekdays: readonly weekdayModel[];
  execution_days: string[] = [];

  taskSchedulingForm!: FormGroup<TaskSchedulingFormGroup>;

  private everyValueChangesSubscription?: Subscription;
  private recurringValueChangesSubscription?: Subscription;

  constructor(
    scheduledTaskService: ScheduledTaskService,
    private controlContainer: ControlContainer,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.weekdays = scheduledTaskService.weekdays;
  }

  ngOnInit(): void {
    this.taskSchedulingForm = this.controlContainer.control?.get(
      'schedulingSelection'
    ) as FormGroup<TaskSchedulingFormGroup>;
    this.lockUnlockControls();

    // Subscribe to the 'every' control value changes
    this.everyValueChangesSubscription = this.formEvery.valueChanges.subscribe((value) => {
      if (value !== null) {
        this.onEveryValueChange(value);
      }
    });

    // Subscribe to recurring type changes
    this.recurringValueChangesSubscription = this.formRecurring.valueChanges.subscribe((value) => {
      if (value !== null && value !== '') {
        this.updateControlsBasedOnRepetitionType(value);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.locked &&
      !changes.locked.firstChange &&
      changes.locked.previousValue != changes.locked.currentValue
    ) {
      this.lockUnlockControls();
    }
  }

  ngOnDestroy(): void {
    // Clean up subscriptions to prevent memory leaks
    if (this.everyValueChangesSubscription) {
      this.everyValueChangesSubscription.unsubscribe();
    }

    if (this.recurringValueChangesSubscription) {
      this.recurringValueChangesSubscription.unsubscribe();
    }
  }

  // Form control accessors for better type safety
  get formEnableScheduling(): FormControl<boolean | null> {
    return this.taskSchedulingForm.controls.enableScheduling;
  }

  get formScheduleDate(): FormControl<string | null> {
    return this.taskSchedulingForm.controls.scheduleDate;
  }

  get formScheduleTime(): FormControl<string | null> {
    return this.taskSchedulingForm.controls.scheduleTime;
  }

  get formRepeatTaskToggle(): FormControl<boolean | null> {
    return this.taskSchedulingForm.controls.repeatTaskToggle;
  }

  get formRecurring(): FormControl<RepetitionOptionsEnum | '' | null> {
    return this.taskSchedulingForm.controls.recurring;
  }

  get formEvery(): FormControl<number | null> {
    return this.taskSchedulingForm.controls.every;
  }

  get formWeekdaysControls(): FormGroup {
    return this.taskSchedulingForm.controls.weekdaysControls;
  }

  get formMonthlyPatternType(): FormControl<MonthlyPatternType | null> {
    return this.taskSchedulingForm.controls.monthlyPatternType;
  }

  lockUnlockControls(): void {
    if (this.locked) {
      this.taskSchedulingForm.disable();
    } else {
      // We have to manually enable the scheduling toggle,
      // but the remaining controls' status will be correctly handled by the on*ToggleChange methods.
      this.formEnableScheduling.enable();
      this.onSchedulingToggleChange();
      this.onRepeatTaskToggleChange();
    }
  }

  /**
   * Handles changes to the 'every' repetition cycle value
   * When repetition is set to WEEKDAILY (every weekday) and 'every' value is > 1,
   * switches the repetition type to WEEKLY
   */
  onEveryValueChange(value: number): void {
    // If 'every' value is greater than 1 and repetition is currently 'every weekday'
    if (value > 1 && this.formRecurring.value === RepetitionOptionsEnum.WEEKDAILY) {
      // Change repetition type to 'weekly' since 'every 2+ weekdays' doesn't make logical sense
      this.formRecurring.patchValue(RepetitionOptionsEnum.WEEKLY);
      // The formRecurring valueChanges subscription will handle the updates
    }
  }

  onSchedulingToggleChange(): void {
    const isSchedulingEnabled = this.formEnableScheduling.value;

    if (isSchedulingEnabled) {
      this.formScheduleDate.enable();
      this.formScheduleTime.enable();
      this.formRepeatTaskToggle.enable();
      this.formMonthlyPatternType.enable();
    } else {
      this.formScheduleDate.disable({ emitEvent: false });
      this.formScheduleTime.disable({ emitEvent: false });
      this.formRepeatTaskToggle.disable({ emitEvent: false });
      this.formRepeatTaskToggle.patchValue(false, { emitEvent: false });
      this.formRecurring.disable({ emitEvent: false });
      this.formEvery.disable({ emitEvent: false });
      this.formWeekdaysControls.disable({ emitEvent: false });
      this.formMonthlyPatternType.disable({ emitEvent: false });

      // Disable weekday controls
      this.disableAllWeekdayControls();
    }
  }

  onRepeatTaskToggleChange(): void {
    const isRepeatEnabled = this.formRepeatTaskToggle.value;

    if (isRepeatEnabled) {
      // Enable relevant controls
      this.formRecurring.enable();
      this.formEvery.enable();

      // Set default recurring value if empty
      if (!this.formRecurring.value) {
        this.formRecurring.patchValue(RepetitionOptionsEnum.WEEKLY);
        // Selection of current day will be handled by updateControlsBasedOnRepetitionType
      } else {
        // Update controls based on current repetition type
        this.updateControlsBasedOnRepetitionType(this.formRecurring.value);
      }
    } else {
      // Disable all repetition-related controls
      this.formRecurring.disable();
      this.formEvery.disable();
      this.disableAllWeekdayControls();
      this.formMonthlyPatternType.disable();
    }
  }

  /**
   * Updates the form controls based on the selected repetition type
   * This is called by the subscription to formRecurring.valueChanges
   */
  private updateControlsBasedOnRepetitionType(value: RepetitionOptionsEnum | ''): void {
    if (!value) return;

    // Check if user selected WEEKDAILY but "every" is > 1
    if (value === RepetitionOptionsEnum.WEEKDAILY && (this.formEvery.value ?? 0) > 1) {
      // Reset "every" value to 1
      this.formEvery.patchValue(1, { emitEvent: false });
    }

    // Preserve the current 'every' value
    const currentEveryValue = this.formEvery.value;

    // Reset weekdays selection (with emitEvent: false to prevent cycles)
    this.formWeekdaysControls.reset({}, { emitEvent: false });
    this.execution_days = [];

    switch (value) {
      case RepetitionOptionsEnum.DAILY:
        this.formWeekdaysControls.enable({ emitEvent: false });
        this.selectAllWeekdays();
        break;

      case RepetitionOptionsEnum.WEEKDAILY:
        this.formWeekdaysControls.enable({ emitEvent: false });
        this.selectWeekdaysOnly();
        break;

      case RepetitionOptionsEnum.WEEKLY:
        this.formWeekdaysControls.enable({ emitEvent: false });
        this.setScheduledDayAsSelected();
        break;

      case RepetitionOptionsEnum.MONTHLY:
        this.disableAllWeekdayControls();
        this.formMonthlyPatternType.enable({ emitEvent: false });
        break;

      case RepetitionOptionsEnum.YEARLY:
        this.disableAllWeekdayControls();
        break;
    }

    // Restore the 'every' value after changing repetition type
    if (currentEveryValue) {
      this.formEvery.patchValue(currentEveryValue, { emitEvent: false });
    }
  }

  /**
   * Selects all weekdays in the form
   */
  private selectAllWeekdays(): void {
    const weekdayValues: Record<string, boolean> = {};

    this.weekdays.forEach((day) => {
      weekdayValues[day.key] = true;
      this.execution_days.push(day.key);
    });

    // Update form values without emitting events
    this.formWeekdaysControls.patchValue(weekdayValues, { emitEvent: false });
  }

  /**
   * Selects only weekdays (Mon-Fri) in the form
   */
  private selectWeekdaysOnly(): void {
    const weekdayValues: Record<string, boolean> = {};

    this.weekdays.forEach((day) => {
      const isWeekday = day.key !== DayOfWeek.SATURDAY && day.key !== DayOfWeek.SUNDAY;
      weekdayValues[day.key] = isWeekday;

      if (isWeekday) {
        this.execution_days.push(day.key);
      }
    });

    // Update form values without emitting events
    this.formWeekdaysControls.patchValue(weekdayValues, { emitEvent: false });
  }

  /**
   * Sets the current scheduled day as selected in weekday controls
   */
  private setScheduledDayAsSelected(): void {
    const scheduledDayKey = this.getScheduledDayKey();

    if (scheduledDayKey) {
      // Update the form control
      const weekdayValues: Record<string, boolean> = {};
      weekdayValues[scheduledDayKey] = true;

      this.formWeekdaysControls.patchValue(weekdayValues, { emitEvent: false });
      this.execution_days = [scheduledDayKey];

      // Disable the control to prevent deselection
      const control = this.formWeekdaysControls.get(scheduledDayKey);
      if (control) {
        control.disable({ emitEvent: false });
      }
    }
  }

  /**
   * Utility method to disable all weekday controls and clear their values
   */
  private disableAllWeekdayControls(): void {
    // Batch update all controls to false without triggering events
    const resetValues: Record<string, boolean> = {};
    this.weekdays.forEach((day) => {
      resetValues[day.key] = false;
    });

    this.formWeekdaysControls.patchValue(resetValues, { emitEvent: false });
    this.formWeekdaysControls.disable({ emitEvent: false });
    this.execution_days = [];
  }

  /**
   * Handles changes to weekday checkboxes
   */
  onCheckboxesChange(key: string, event: boolean): void {
    // Update the execution_days array
    this.updateExecutionDaysArray(key, event);

    // Compute the appropriate repetition type based on selection
    const newRepetitionType = this.computeRepetitionType();

    // Apply the computed repetition type without triggering valueChanges events
    if (this.formRecurring.value !== newRepetitionType) {
      this.formRecurring.patchValue(newRepetitionType, { emitEvent: false });
    }

    // Update control states
    this.updateWeekdayControlsState();
  }

  /**
   * Updates the execution_days array based on checkbox changes
   */
  private updateExecutionDaysArray(key: string, isChecked: boolean): void {
    if (isChecked) {
      this.execution_days.push(key);
    } else {
      const index = this.execution_days.findIndex((item) => item === key);
      if (index >= 0) {
        this.execution_days.splice(index, 1);
      }
    }
  }

  /**
   * Computes the appropriate repetition type based on selected weekdays
   */
  private computeRepetitionType(): RepetitionOptionsEnum {
    // If all 7 weekdays are selected and 'every' is 1, use DAILY
    if (this.execution_days.length === 7 && this.formEvery.value === 1) {
      return RepetitionOptionsEnum.DAILY;
    }

    // If only the 5 weekdays (Mon-Fri) are selected and 'every' is 1, use WEEKDAILY
    if (
      this.execution_days.length === 5 &&
      !this.execution_days.includes(DayOfWeek.SATURDAY) &&
      !this.execution_days.includes(DayOfWeek.SUNDAY) &&
      this.formEvery.value === 1
    ) {
      return RepetitionOptionsEnum.WEEKDAILY;
    }

    // In all other cases, use WEEKLY
    return RepetitionOptionsEnum.WEEKLY;
  }

  /**
   * Updates the weekday control states based on the execution_days array
   */
  private updateWeekdayControlsState(): void {
    // Enable all controls first
    this.formWeekdaysControls.enable({ emitEvent: false });

    // If only one day is selected, disable it to ensure at least one selection
    if (this.execution_days.length === 1) {
      const control = this.formWeekdaysControls.get(this.execution_days[0]);
      if (control) {
        control.disable({ emitEvent: false });
      }
    }
  }

  /**
   * Checks if the scheduled date and time are in the past
   */
  checkScheduleDateTime(): void {
    const timeValue = this.formScheduleTime.value;
    const dateValue = this.formScheduleDate.value;

    if (!timeValue || !dateValue) {
      this.taskScheduledInPast = false;
      return;
    }

    const timeParts = timeValue.split(':');
    const hours = parseInt(timeParts[0]);
    const minutes = parseInt(timeParts[1]);

    const scheduleDateTime = new Date(dateValue);
    scheduleDateTime.setHours(hours, minutes);

    this.taskScheduledInPast = new Date().valueOf() > scheduleDateTime.valueOf();
  }

  getScheduledDayKey(): string | undefined {
    const dateValue = this.formScheduleDate.value;
    if (!dateValue) return undefined;

    const scheduledDayIndex = new Date(dateValue).getDay();
    return this.weekdays.find((day) => day.dayIndex === scheduledDayIndex)?.key;
  }

  /**
   * Generates a human-readable summary of the repetition schedule
   */
  getSummaryText(): string {
    const recurringValue = this.formRecurring.value;

    let baseText = '';
    switch (recurringValue) {
      case RepetitionOptionsEnum.WEEKLY:
        const selectedDays = this.getSelectedWeekdays();
        if (selectedDays.length > 0) {
          baseText = $localize`:Summary Repeat Text@@summaryRepeatText:, ${this.formatWeekdayList(
            selectedDays
          )}`;
        }
        return baseText;
      case RepetitionOptionsEnum.MONTHLY:
        const patternType = this.formMonthlyPatternType.value;

        if (patternType === MonthlyPatternType.POSITION) {
          // Standard position-based pattern (e.g., "third Monday")
          baseText = $localize`:Summary Repeat Text@@summaryRepeatText:, ${this.getPositionText()}`;
        } else if (patternType === MonthlyPatternType.FOURTH) {
          // Fourth weekday pattern
          baseText = $localize`:Summary Repeat Text@@summaryRepeatText:, ${this.getFourthPositionText()}`;
        } else if (patternType === MonthlyPatternType.LAST) {
          // Last weekday pattern
          baseText = $localize`:Summary Repeat Text@@summaryRepeatText:, ${this.getLastPositionText()}`;
        } else {
          // Date-based pattern (e.g., "16th")
          baseText = $localize`:Summary Repeat Text@@summaryRepeatText:, ${this.getDateText()}`;
        }
        return baseText;
      case RepetitionOptionsEnum.YEARLY:
        const dateValue = this.formScheduleDate.value;
        if (!dateValue) return '';

        const scheduleDate = new Date(dateValue);
        const yearlyDesc = getYearlyDescription(scheduleDate, this.locale);
        baseText = $localize`:Summary Repeat Text@@summaryRepeatText:, ${yearlyDesc}`;
        return baseText;
      default:
        //needed this for german language 'wiederholt' in 'Dieser Task wird alle Tage wiederholt, beginnend am 18.04.25 um 00:00'
        baseText = $localize`:Summary Repeat Text@@summaryRepeatTextEmpty:`;
        return baseText;
    }
  }

  /**
   * Gets array of selected weekday display names
   */
  private getSelectedWeekdays(): string[] {
    const selectedDays: string[] = [];
    const weekdaysGroup = this.formWeekdaysControls;

    this.weekdays.forEach((day) => {
      if (weekdaysGroup.get(day.key)?.value === true) {
        selectedDays.push(day.value);
      }
    });

    return selectedDays;
  }

  /**
   * Formats an array of weekday names into a readable string
   */
  private formatWeekdayList(days: string[]): string {
    if (days.length === 0) return '';
    if (days.length === 1) return days[0];

    // For 2 days, use a simple 'and' conjunction
    if (days.length === 2) {
      return $localize`:Two weekdays in list@@summaryTwoWeekdays:${days[0]} and ${days[1]}`;
    }

    // For 3 or more days, use comma-separated list with 'and' before the last item
    const lastDay = days[days.length - 1];
    const otherDays = days.slice(0, days.length - 1);
    return $localize`:Multiple weekdays in list@@summaryMultipleWeekdaysInList:${otherDays.join(
      ', '
    )}, and ${lastDay}`;
  }

  /**
   * Checks if the selected date is the fourth occurrence of its weekday in the month
   * Also sets the default radio button value to 'fourth' when this condition is met
   */
  isFourthWeekday(): boolean {
    const dateValue = this.formScheduleDate.value;
    if (!dateValue) return false;

    const date = new Date(dateValue);
    const isFourth = getWeekdayPositionInMonth(date) === 'fourth';

    // If it's a fourth weekday and the form is enabled, set the default value to 'fourth'
    if (isFourth && !this.formMonthlyPatternType.disabled) {
      // Only update if the current value is 'position' (the standard option)
      // This prevents overriding a user's explicit choice of 'last' or 'date'
      if (this.formMonthlyPatternType.value === MonthlyPatternType.POSITION) {
        this.formMonthlyPatternType.setValue(MonthlyPatternType.FOURTH);
      }
    } else if (!isFourth && !this.formMonthlyPatternType.disabled) {
      // For non-fourth weekdays, we need to ensure they're using 'position' or 'date'
      // but only if the current value is 'fourth' or 'last' which aren't applicable
      const currentValue = this.formMonthlyPatternType.value;
      if (currentValue === MonthlyPatternType.FOURTH || currentValue === MonthlyPatternType.LAST) {
        this.formMonthlyPatternType.setValue(MonthlyPatternType.POSITION);
      }
    }

    return isFourth;
  }

  /**
   * Gets the text for the fourth position option (e.g., "fourth Monday")
   */
  getFourthPositionText(): string {
    const dateValue = this.formScheduleDate.value;
    if (!dateValue) return '';

    const date = new Date(dateValue);
    const weekdayName = date.toLocaleString(this.locale, { weekday: 'long' });
    return $localize`:Fourth weekday position@@summaryFourthWeekday:fourth ${weekdayName}`;
  }

  /**
   * Gets the text for the last position option (e.g., "last Monday")
   */
  getLastPositionText(): string {
    return getLastPositionText(this.taskSchedulingForm.controls.scheduleDate.value!, this.locale);
  }

  /**
   * Gets the text for the standard position-based option (e.g., "third Monday")
   */
  getPositionText(): string {
    return getPositionText(this.taskSchedulingForm.controls.scheduleDate.value!, this.locale);
  }

  /**
   * Gets the text for the date-based option (e.g., "16th")
   * This method properly supports localization for ordinal numbers
   */
  getDateText(): string {
    return getDateText(this.taskSchedulingForm.controls.scheduleDate.value!);
  }
}
